import {
  LayoutPanelLeft,
  Plug,
  Layers,
  Brush,
  Hammer,
  Square,
  Wrench,
  SprayCan,
  ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useReveal } from "@/lib/useReveal";

const items = [
  { id: "partitioning", to: "/services/partitioning", title: "Partitioning", icon: LayoutPanelLeft, copy: "Demountable, acoustic and structural partitions engineered to brief." },
  { id: "me", to: "/services/mechanical-electrical", title: "M&E", icon: Plug, copy: "Mechanical and electrical design-and-install, fully coordinated." },
  { id: "flooring", to: "/services/flooring", title: "Flooring", icon: Layers, copy: "Resin, timber, vinyl and tile — laid by our own trades." },
  { id: "painting", to: "/services/painting-decoration", title: "Painting & Decoration", icon: Brush, copy: "Spec-grade finishes, hand-detailed by in-house decorators." },
  { id: "joinery", to: "/services/manufactured-joinery", title: "Manufactured Joinery", icon: Hammer, copy: "Bespoke joinery built by our own makers in our workshop." },
  { id: "solid-surface", to: "/services/solid-surface", title: "Solid Surface", icon: Square, copy: "CNC-fabricated Corian-grade surfaces for bars, counters & vanities." },
  { id: "metalwork", to: "/services/metalwork", title: "Metalwork", icon: Wrench, copy: "Welded, machined and finished — handrails to feature frames." },
  { id: "spray-finishes", to: "/services/spray-finishes", title: "Spray Finishes", icon: SprayCan, copy: "Dedicated workshop spray facility for joinery, panels and metalwork." },
];

export const Capabilities = () => {
  const ref = useReveal();
  return (
    <section
      id="capabilities"
      ref={ref}
      data-testid="capabilities-section"
      className="relative py-24 lg:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 mb-14 lg:mb-20">
          <div className="col-span-12 lg:col-span-3 reveal">
            <div className="overline">02 — Capabilities</div>
          </div>
          <div className="col-span-12 lg:col-span-9 reveal">
            <h2
              data-testid="capabilities-heading"
              className="font-display text-3xl sm:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.02] text-balance"
            >
              All trades. One team. <span className="text-black/45">No subcontractor relay.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-black/65 leading-relaxed">
              Anderston carries every trade in-house — which means one schedule,
              one accountable team, and the design intent preserved from drawing
              to handover.
            </p>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-12 border-t border-l hairline reveal">
          {items.map((it, idx) => {
            const Icon = it.icon;
            const wide = idx === 0 || idx === 4;
            return (
              <Link
                key={it.id}
                to={it.to}
                data-testid={`capability-${it.id}`}
                className={`group relative p-7 lg:p-9 border-b border-r hairline min-h-[210px] flex flex-col justify-between bg-white hover:bg-[#0A0A0A] hover:text-white transition-colors duration-300 ${
                  wide ? "col-span-12 sm:col-span-6 lg:col-span-6" : "col-span-12 sm:col-span-6 lg:col-span-3"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center justify-center w-10 h-10 border hairline group-hover:border-white/30">
                    <Icon size={18} />
                  </div>
                  <span className="overline group-hover:text-white/70">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
                <div>
                  <div className="font-display text-2xl lg:text-3xl tracking-tight">
                    {it.title}
                  </div>
                  <p className="text-sm mt-3 max-w-xs opacity-70">{it.copy}</p>
                </div>
                <ArrowUpRight
                  size={18}
                  className="absolute top-7 right-7 opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Capabilities;
